import type { FormFieldType, FormFieldOption, FieldCondition } from '@hcc/types';
import { OPTION_FIELD_TYPES } from '@hcc/types';

/** A field a condition can be driven by. */
export interface ConditionSourceField {
  key: string;
  label: string;
  type: FormFieldType;
  options?: FormFieldOption[];
}

/** The selectable answers for a driver field, or null for free-text/number. */
function conditionOptions(f: ConditionSourceField | undefined): FormFieldOption[] | null {
  if (!f) return null;
  if (f.type === 'yes_no') return [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
  if (OPTION_FIELD_TYPES.includes(f.type)) return f.options ?? [];
  return null;
}

const inputClass =
  'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

interface ConditionEditorProps {
  condition: FieldCondition | undefined;
  /** Candidate driver fields (other fields, or earlier-page fields). */
  sources: ConditionSourceField[];
  onChange: (next: FieldCondition | undefined) => void;
  /** Label for the enable checkbox. */
  label: string;
  /** Shown when there are no candidate driver fields. */
  emptyHint?: string;
}

/**
 * Shared "only show when X is Y" editor, used for both field-level and
 * page-level conditional visibility. Stores the driver field's key and the
 * matching option value(s) so wording can be reworded without breaking it.
 */
export function ConditionEditor({ condition, sources, onChange, label, emptyHint }: ConditionEditorProps) {
  const cond = condition;
  const source = cond ? sources.find((s) => s.key === cond.field) : undefined;
  const choices = conditionOptions(source);

  function toggleValue(value: string) {
    if (!cond) return;
    const next = cond.equals.includes(value)
      ? cond.equals.filter((v) => v !== value)
      : [...cond.equals, value];
    onChange({ field: cond.field, equals: next });
  }

  if (sources.length === 0) {
    return (
      <div className="rounded-token-sm border border-line/10 p-3 text-sm text-fg-dim">
        {emptyHint ?? 'No earlier fields to base a condition on yet.'}
      </div>
    );
  }

  return (
    <div className="rounded-token-sm border border-line/10 p-3">
      <label className="flex items-center gap-2 text-sm font-medium text-fg">
        <input
          type="checkbox"
          checked={!!cond}
          onChange={(e) => onChange(e.target.checked ? { field: sources[0]!.key, equals: [] } : undefined)}
        />
        {label}
      </label>
      {cond ? (
        <div className="mt-2 space-y-2">
          <select
            value={cond.field}
            // A different driver has a different (or no) answer set; reset values.
            onChange={(e) => onChange({ field: e.target.value, equals: [] })}
            className={inputClass}
          >
            {sources.map((o) => (
              <option key={o.key} value={o.key}>{o.label} ({o.key})</option>
            ))}
          </select>
          {choices ? (
            <fieldset className="block text-sm">
              <span className="font-medium text-fg-muted">Show when the answer is</span>
              {choices.length === 0 ? (
                <p className="mt-1 text-xs text-fg-dim">That field has no options yet. Add options to it first.</p>
              ) : (
                <div className="mt-1.5 flex flex-col gap-1.5">
                  {choices.map((o) => (
                    <label key={o.value} className="flex items-center gap-1.5 text-fg">
                      <input type="checkbox" checked={cond.equals.includes(o.value)} onChange={() => toggleValue(o.value)} />
                      {o.label}
                      {o.label !== o.value ? <span className="text-xs text-fg-dim">({o.value})</span> : null}
                    </label>
                  ))}
                </div>
              )}
              <span className="mt-1 block text-xs text-fg-dim">Tick one or more. Shows when the answer matches any ticked value.</span>
            </fieldset>
          ) : (
            <label className="block text-sm">
              <span className="font-medium text-fg-muted">Show when value is one of</span>
              <input
                value={cond.equals.join(', ')}
                onChange={(e) =>
                  onChange({ field: cond.field, equals: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
                }
                className={inputClass}
                placeholder="e.g. 1234"
              />
              <span className="mt-1 block text-xs text-fg-dim">Comma-separated. This field has free-text answers, so type the values to match.</span>
            </label>
          )}
        </div>
      ) : null}
    </div>
  );
}
